// src/pages/AlarmsDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMonitoringWebSocket } from '../hooks/useMonitoringWebSocket';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  TextField,
  Autocomplete,
  Button,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Skeleton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  LocalHospital as EmergencyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  NotificationsActive as ActiveIcon,
  DeviceHub as DeviceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  FilterList as FilterListIcon,
  PriorityHigh as CriticalIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelledIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, subHours, subDays, isAfter, differenceInMinutes } from 'date-fns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { usePersistedFilter, FILTER_KEYS } from '../utils/filterPersistence';
import { useNavigate } from 'react-router-dom';
import { alarmsApi, locationsApi } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useSiteFilter } from '../contexts/SiteFilterContext';
import { useIsMobile } from '../hooks/useResponsive';
import Home from '@mui/icons-material/Home';

// Status color mapping based on AlarmStates table
const getStatusColor = (stateCode) => {
  switch (stateCode) {
    // Active/urgent states
    case 'countdown':
      return 'warning';
    case 'sent_to_emergency':
    case 'in_call':
      return 'error';
    // Pending states
    case 'initiated':
    case 'routed':
    case 'pending':
      return 'info';
    // Resolution states
    case 'acknowledged':
      return 'info';
    case 'dispatched':
      return 'primary';
    // Terminal states
    case 'finished':
    case 'resolved':
      return 'success';
    case 'cancelled':
    case 'dropped':
      return 'default';
    default:
      return 'default';
  }
};

// Status icon mapping
const getStatusIcon = (stateCode) => {
  switch (stateCode) {
    case 'initiated':
      return <InfoIcon fontSize="small" />;
    case 'countdown':
      return <ScheduleIcon fontSize="small" />;
    case 'routed':
    case 'pending':
      return <ScheduleIcon fontSize="small" />;
    case 'in_call':
    case 'sent_to_emergency':
      return <ErrorIcon fontSize="small" />;
    case 'acknowledged':
      return <InfoIcon fontSize="small" />;
    case 'dispatched':
      return <EmergencyIcon fontSize="small" />;
    case 'finished':
    case 'resolved':
      return <CheckCircleIcon fontSize="small" />;
    case 'cancelled':
    case 'dropped':
      return <WarningIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

// Get best location info from alarm data
// Returns { alarmLocation, alarmLocationSource, personHomeLocation, isAtHome }
// - alarmLocation: Where the alarm was triggered (antenna signal or direct location)
// - personHomeLocation: Person's assigned/home location (if different from alarm location)
// - isAtHome: True if person is at their home location
const getBestLocationInfo = (alarm) => {
  const antennas = alarm?.Content?.antennas || [];
  const locationConfidence = alarm?.Content?.locationConfidence;

  // Resolve person from multiple sources: direct personModel, or from device
  const person = alarm?.personModel || alarm?.deviceModel?.personModel;
  const personLocationId = person?.personLocation || alarm?.Content?.personLocationId;

  // Get person's home location name
  const personHomeName = person?.locationModel?.friendlyName ||
                         alarm?.Content?.personLocationName ||
                         (personLocationId && alarm?.locationModel?.id === personLocationId ? alarm?.locationModel?.friendlyName : null);
  const personHomeLocation = personLocationId ? (personHomeName || `Location ${personLocationId}`) : null;

  // Get strongest antenna location (where signal was detected)
  const strongestAntenna = antennas.length > 0
    ? antennas.reduce((best, ant) => (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null)
    : null;
  const strongestAntennaLocation = strongestAntenna?.locationName ||
    (strongestAntenna?.locationId ? `Location ${strongestAntenna.locationId}` : null);
  const strongestAntennaLocationId = strongestAntenna?.locationId;

  // Determine alarm location (where the alarm was triggered)
  let alarmLocation = null;
  let alarmLocationSource = null;

  // Priority 1: Antenna signal location
  if (strongestAntennaLocation) {
    alarmLocation = strongestAntennaLocation;
    alarmLocationSource = 'signal';
  }
  // Priority 2: Direct location from alarm
  else if (alarm?.location || alarm?.locationModel?.friendlyName) {
    alarmLocation = alarm.location || alarm.locationModel.friendlyName;
    alarmLocationSource = 'direct';
  }
  // Priority 3: Device location
  else if (alarm?.deviceModel?.locationModel?.friendlyName) {
    alarmLocation = alarm.deviceModel.locationModel.friendlyName;
    alarmLocationSource = 'device';
  }
  // Priority 4: Person's home (if no other location data)
  else if (personHomeLocation) {
    alarmLocation = personHomeLocation;
    alarmLocationSource = 'home';
  }

  // Check if person is at home (alarm location matches person's home)
  const isAtHome = personLocationId && strongestAntennaLocationId
    ? Number(strongestAntennaLocationId) === Number(personLocationId)
    : alarmLocationSource === 'home';

  return {
    alarmLocation,
    alarmLocationSource,
    personHomeLocation: isAtHome ? null : personHomeLocation, // Only show home if different from alarm location
    isAtHome,
    confidence: locationConfidence || (alarmLocationSource === 'signal' ? 'medium' : 'high'),
    // Legacy fields for backwards compatibility
    name: alarmLocation,
    source: alarmLocationSource,
    nearestLocation: null
  };
};

// Legacy function for backwards compatibility
const getBestLocationName = (alarm) => {
  const info = getBestLocationInfo(alarm);
  return info?.name || null;
};

const AlarmsDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();
  const isMobile = useIsMobile();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active alarms
  const [activeAlarms, setActiveAlarms] = useState([]);

  // Recent alarms (last 24h)
  const [recentAlarms, setRecentAlarms] = useState([]);

  // History
  const [historyAlarms, setHistoryAlarms] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(25);

  // Filters for history
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Enhanced filters with persistence
  const [searchTerm, setSearchTerm] = usePersistedFilter(FILTER_KEYS.ALARM.SEARCH, '');
  const [quickFilter, setQuickFilter] = usePersistedFilter(FILTER_KEYS.ALARM.QUICK_FILTER, 'all');
  const [dateFrom, setDateFrom] = usePersistedFilter(FILTER_KEYS.ALARM.DATE_FROM, null);
  const [dateTo, setDateTo] = usePersistedFilter(FILTER_KEYS.ALARM.DATE_TO, null);
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const searchDebounceRef = useRef(null);

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // Audio alert state
  const [audioEnabled, setAudioEnabled] = usePersistedFilter(FILTER_KEYS.ALARM.AUDIO_ENABLED, false);
  const [seenAlarmIds, setSeenAlarmIds] = useState(new Set());
  const audioContextRef = useRef(null);
  const lastSoundTimeRef = useRef(0);

  // Refs for load functions so WebSocket handler always calls the latest version
  const loadActiveAndRecentAlarmsRef = useRef(null);
  const loadAlarmHistoryRef = useRef(null);

  // Debounced search update
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(localSearch);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [localSearch, setSearchTerm]);

  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    // Handle alarm-related events
    if (message.type === 'alarm_update' || message.type === 'alarm_created' ||
        message.type === 'alarm_status_change' || message.type === 'alarm' ||
        message.type === 'alarm_closed') {
      console.log('[AlarmsDashboard] Received alarm update:', message);

      // Handle alarm_closed - remove from active list immediately
      if (message.type === 'alarm_closed') {
        const closedAlarmId = message.alarmId || message.alarm_id || message.data?.id;
        if (closedAlarmId) {
          setActiveAlarms(prev => prev.filter(a => a.id !== closedAlarmId));
        }
      }

      // Handle status change - update the alarm in the list optimistically
      if (message.type === 'alarm_status_change' && message.data) {
        const updatedAlarm = message.data;
        const alarmId = updatedAlarm.id || message.alarmId;

        // If alarm became terminal, remove from active and add to recent
        if (updatedAlarm.alarmStateModel?.isTerminal || updatedAlarm.isTerminal) {
          setActiveAlarms(prev => prev.filter(a => a.id !== alarmId));
        } else {
          // Update the alarm in the active list
          setActiveAlarms(prev => prev.map(a =>
            a.id === alarmId ? { ...a, ...updatedAlarm } : a
          ));
        }
      }

      // Refresh alarms for full update (use refs to always call the latest version)
      if (tabValue === 0) {
        loadActiveAndRecentAlarmsRef.current?.();
      } else {
        loadAlarmHistoryRef.current?.();
      }
    }
  }, [tabValue]);

  // WebSocket connection for real-time updates
  const { isConnected, connectionStatus } = useMonitoringWebSocket({
    autoConnect: true,
    onMessage: handleWebSocketMessage
  });

  // Fallback polling if WebSocket disconnects (30 second interval)
  useEffect(() => {
    if (isConnected) return; // Don't poll if WebSocket is connected

    const interval = setInterval(() => {
      if (tabValue === 0) {
        loadActiveAndRecentAlarms();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [tabValue, isConnected]);

  // Memoized stats calculations (performance optimization)
  const stats = useMemo(() => ({
    activeCount: activeAlarms.length,
    recentCount: recentAlarms.length,
    pendingCount: activeAlarms.filter(a =>
      a.stateCode === 'countdown' || a.stateCode === 'sent_to_emergency'
    ).length,
    resolvedCount: recentAlarms.filter(a => a.isTerminal).length
  }), [activeAlarms, recentAlarms]);

  // Memoized site filter function (removes duplicate code)
  const filterAlarmsBySite = useCallback((alarms) => {
    if (!hasSiteFilter || !siteLocationIds) return alarms;
    return alarms.filter(alarm => {
      const alarmLocationId = alarm.locationId || alarm.location?.id || alarm.locationModel?.id;
      return siteLocationIds.includes(Number(alarmLocationId)) ||
             siteLocationIds.includes(String(alarmLocationId));
    });
  }, [hasSiteFilter, siteLocationIds]);

  // Filter alarms by search term
  const filterBySearch = useCallback((alarms) => {
    if (!searchTerm) return alarms;
    const term = searchTerm.toLowerCase();
    return alarms.filter(alarm => {
      const person = alarm.person?.toLowerCase() || '';
      const device = alarm.device?.toLowerCase() || '';
      const location = getBestLocationInfo(alarm)?.alarmLocation?.toLowerCase() || '';
      const type = alarm.type?.toLowerCase() || '';
      const state = alarm.state?.toLowerCase() || '';
      return person.includes(term) || device.includes(term) || location.includes(term) || type.includes(term) || state.includes(term);
    });
  }, [searchTerm]);

  // Filter alarms by quick filter
  const filterByQuickFilter = useCallback((alarms) => {
    if (!quickFilter || quickFilter === 'all') return alarms;
    return alarms.filter(alarm => {
      switch (quickFilter) {
        case 'critical':
          return alarm.stateCode === 'countdown' || alarm.stateCode === 'sent_to_emergency' || alarm.stateCode === 'in_call';
        case 'pending':
          return alarm.stateCode === 'initiated' || alarm.stateCode === 'routed' || alarm.stateCode === 'pending';
        case 'resolved':
          return alarm.stateCode === 'finished' || alarm.stateCode === 'resolved' || alarm.stateCode === 'acknowledged';
        case 'cancelled':
          return alarm.stateCode === 'cancelled' || alarm.stateCode === 'dropped';
        default:
          return true;
      }
    });
  }, [quickFilter]);

  // Apply all filters to alarms (chain: site -> quick filter -> search)
  const applyFilters = useCallback((alarms) => {
    let filtered = filterByQuickFilter(alarms);
    filtered = filterBySearch(filtered);
    return filtered;
  }, [filterByQuickFilter, filterBySearch]);

  // Filtered alarm lists
  const filteredActiveAlarms = useMemo(() => applyFilters(activeAlarms), [activeAlarms, applyFilters]);
  const filteredRecentAlarms = useMemo(() => applyFilters(recentAlarms), [recentAlarms, applyFilters]);

  // Quick filter counts (for badges)
  const quickFilterCounts = useMemo(() => ({
    all: activeAlarms.length + recentAlarms.length,
    critical: [...activeAlarms, ...recentAlarms].filter(a =>
      a.stateCode === 'countdown' || a.stateCode === 'sent_to_emergency' || a.stateCode === 'in_call'
    ).length,
    pending: [...activeAlarms, ...recentAlarms].filter(a =>
      a.stateCode === 'initiated' || a.stateCode === 'routed' || a.stateCode === 'pending'
    ).length,
    resolved: [...activeAlarms, ...recentAlarms].filter(a =>
      a.stateCode === 'finished' || a.stateCode === 'resolved' || a.stateCode === 'acknowledged'
    ).length,
    cancelled: [...activeAlarms, ...recentAlarms].filter(a =>
      a.stateCode === 'cancelled' || a.stateCode === 'dropped'
    ).length
  }), [activeAlarms, recentAlarms]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setLocalSearch('');
    setSearchTerm('');
    setQuickFilter('all');
    setDateFrom(null);
    setDateTo(null);
  }, [setSearchTerm, setQuickFilter, setDateFrom, setDateTo]);

  // Check if any filters are active
  const hasActiveFilters = searchTerm || (quickFilter && quickFilter !== 'all') || dateFrom || dateTo;

  // Load data on mount
  useEffect(() => {
    loadActiveAndRecentAlarms();
    loadLocations();
  }, [selectedSiteId, siteLocationIds]);

  // Load history when tab changes or filters change
  useEffect(() => {
    if (tabValue === 1) {
      loadAlarmHistory();
    }
  }, [tabValue, historyPage, historyRowsPerPage, selectedLocation, selectedStatus, dateFrom, dateTo, searchTerm]);

  const loadActiveAndRecentAlarms = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load active alarms
      const activeResponse = await alarmsApi.getActiveAlarms();
      const activeData = filterAlarmsBySite(activeResponse.data?.data || []);
      setActiveAlarms(activeData);

      // Load recent alarms (last 24 hours) - we'll filter from all history
      const historyResponse = await alarmsApi.getAlarmHistory({ limit: 100 });
      const allAlarms = filterAlarmsBySite(historyResponse.data?.data?.alarms || []);

      // Filter to last 24 hours
      const twentyFourHoursAgo = subHours(new Date(), 24);
      const recent = allAlarms.filter(alarm =>
        isAfter(new Date(alarm.firstPressAt), twentyFourHoursAgo)
      );
      setRecentAlarms(recent);

    } catch (err) {
      console.error('Failed to load alarms:', err);
      setError('Failed to load alarms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await locationsApi.getAll({ limit: 1000 });
      setLocations(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadAlarmHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: historyRowsPerPage,
        offset: historyPage * historyRowsPerPage
      };

      if (selectedLocation) {
        params.locationId = selectedLocation.id;
      }

      if (selectedStatus) {
        params.stateCode = selectedStatus;
      }

      // Add date range filters
      if (dateFrom) {
        params.startDate = new Date(dateFrom).toISOString();
      }
      if (dateTo) {
        params.endDate = new Date(dateTo).toISOString();
      }

      const response = await alarmsApi.getAlarmHistory(params);
      const data = response.data?.data || {};
      let alarms = filterAlarmsBySite(data.alarms || []);

      // Apply search filter client-side for history
      if (searchTerm) {
        alarms = filterBySearch(alarms);
      }

      setHistoryAlarms(alarms);
      setHistoryTotal(hasSiteFilter || searchTerm ? alarms.length : (data.total || 0));

    } catch (err) {
      console.error('Failed to load alarm history:', err);
      setError('Failed to load alarm history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Keep refs updated so WebSocket handler always calls the latest versions
  loadActiveAndRecentAlarmsRef.current = loadActiveAndRecentAlarms;
  loadAlarmHistoryRef.current = loadAlarmHistory;

  const handleViewAlarm = (alarm) => {
    // Navigate to the alarm details page
    navigate(`/alarms/${alarm.id}`);
  };

  const handleUpdateStatus = async (alarmId, newStatus) => {
    try {
      await alarmsApi.updateAlarmStatus(alarmId, newStatus);
      loadActiveAndRecentAlarms();
    } catch (err) {
      console.error('Failed to update alarm status:', err);
    }
  };

  // Export functions
  const getCurrentAlarms = useCallback(() => {
    if (tabValue === 0) {
      return [...filteredActiveAlarms, ...filteredRecentAlarms];
    } else {
      return historyAlarms;
    }
  }, [tabValue, filteredActiveAlarms, filteredRecentAlarms, historyAlarms]);

  const formatAlarmForExport = (alarm) => {
    const locationInfo = getBestLocationInfo(alarm);
    return {
      id: alarm.id,
      type: alarm.type || 'Unknown',
      status: alarm.state || alarm.stateCode,
      person: alarm.person || '-',
      device: alarm.device || '-',
      location: locationInfo?.alarmLocation || '-',
      personHome: locationInfo?.personHomeLocation || '-',
      firstPressAt: alarm.firstPressAt ? format(new Date(alarm.firstPressAt), 'yyyy-MM-dd HH:mm:ss') : '-',
      lastUpdateAt: alarm.updatedAt ? format(new Date(alarm.updatedAt), 'yyyy-MM-dd HH:mm:ss') : '-',
      notifications: alarm.notificationCount || 0
    };
  };

  const handleExportCSV = () => {
    const alarms = getCurrentAlarms();
    if (alarms.length === 0) {
      setError('No alarms to export');
      return;
    }

    const headers = ['ID', 'Type', 'Status', 'Person', 'Device', 'Location', 'Person Home', 'First Press', 'Last Update', 'Notifications'];
    const rows = alarms.map(alarm => {
      const data = formatAlarmForExport(alarm);
      return [data.id, data.type, data.status, data.person, data.device, data.location, data.personHome, data.firstPressAt, data.lastUpdateAt, data.notifications];
    });

    // Build CSV with BOM for Excel
    const BOM = '\uFEFF';
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm');
    link.setAttribute('href', url);
    link.setAttribute('download', `alarms_export_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const alarms = getCurrentAlarms();
    if (alarms.length === 0) {
      setError('No alarms to export');
      return;
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      totalAlarms: alarms.length,
      filters: {
        search: searchTerm || null,
        quickFilter: quickFilter || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null
      },
      alarms: alarms.map(formatAlarmForExport)
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm');
    link.setAttribute('href', url);
    link.setAttribute('download', `alarms_export_${timestamp}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get or create a reusable AudioContext (lazy initialization avoids browser warning)
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browser requires user gesture first)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Initialize AudioContext on first user interaction with audio toggle
  const handleAudioToggle = useCallback(() => {
    const newValue = !audioEnabled;
    setAudioEnabled(newValue);
    if (newValue) {
      // Create and resume on user gesture so future plays work
      try { getAudioContext(); } catch (e) { /* ignore */ }
    }
  }, [audioEnabled, setAudioEnabled, getAudioContext]);

  // Audio alert function
  const playAlarmSound = useCallback((isUrgent = false) => {
    if (!audioEnabled) return;

    // Debounce - no more than 1 sound per 5 seconds
    const now = Date.now();
    if (now - lastSoundTimeRef.current < 5000) return;
    lastSoundTimeRef.current = now;

    try {
      const audioContext = getAudioContext();

      const playBeep = (freq, delay = 0) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        const startTime = audioContext.currentTime + delay;
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.5);
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.5);
      };

      playBeep(isUrgent ? 880 : 440);

      // For urgent, play 3 beeps
      if (isUrgent) {
        playBeep(880, 0.6);
        playBeep(880, 1.2);
      }
    } catch (error) {
      console.error('Failed to play alarm sound:', error);
    }
  }, [audioEnabled, getAudioContext]);

  // Check for new critical alarms and play sound
  useEffect(() => {
    if (!audioEnabled) return;

    const currentAlarmIds = new Set(activeAlarms.map(a => a.id));
    const newAlarms = activeAlarms.filter(a => !seenAlarmIds.has(a.id));

    if (newAlarms.length > 0) {
      // Check if any new alarms are critical
      const hasCritical = newAlarms.some(a =>
        a.stateCode === 'countdown' || a.stateCode === 'sent_to_emergency' || a.stateCode === 'in_call'
      );

      if (hasCritical) {
        playAlarmSound(true);
      } else if (newAlarms.length > 0) {
        playAlarmSound(false);
      }

      // Update seen alarms
      setSeenAlarmIds(currentAlarmIds);
    }
  }, [activeAlarms, audioEnabled, seenAlarmIds, playAlarmSound]);

  // Mobile card view for a single alarm
  const renderAlarmCard = (alarm) => {
    const locationInfo = getBestLocationInfo(alarm);
    return (
      <Card
        key={alarm.id}
        sx={{
          mb: 2,
          cursor: 'pointer',
          backgroundColor: alarm.stateCode === 'sent_to_emergency' ? 'error.light' :
                           alarm.stateCode === 'countdown' ? 'warning.light' : 'background.paper',
          '&:hover': {
            boxShadow: 4,
            backgroundColor: alarm.stateCode === 'sent_to_emergency' ? 'error.main' :
                             alarm.stateCode === 'countdown' ? 'warning.main' : 'action.hover'
          },
          transition: 'all 0.2s',
          // Pulse animation for critical alarms
          ...(alarm.stateCode === 'sent_to_emergency' && {
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 }
            }
          })
        }}
        onClick={() => handleViewAlarm(alarm)}
      >
        <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header row - Status and Type */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Chip
              icon={getStatusIcon(alarm.stateCode)}
              label={alarm.state}
              color={getStatusColor(alarm.stateCode)}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {alarm.type || 'Unknown'}
            </Typography>
          </Box>

          {/* Location info */}
          {locationInfo && locationInfo.alarmLocation && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  At: {locationInfo.alarmLocation}
                </Typography>
                {locationInfo.isAtHome && (
                  <Chip
                    label="home"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                  />
                )}
              </Box>
              {locationInfo.personHomeLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 0.5 }}>
                  <Home fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                  <Typography variant="caption" color="text.secondary">
                    Home: {locationInfo.personHomeLocation}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Person info */}
          {alarm.person && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">{alarm.person}</Typography>
            </Box>
          )}

          {/* Device info */}
          {alarm.device && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <DeviceIcon fontSize="small" color="action" />
              <Typography variant="body2">{alarm.device}</Typography>
            </Box>
          )}

          {/* Footer - Time */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon fontSize="small" />
              {formatDistanceToNow(new Date(alarm.firstPressAt), { addSuffix: true })}
            </Typography>
            <Chip
              label={`${alarm.notificationCount || 0} notifications`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Mobile card list view
  const renderAlarmCards = (alarms) => (
    <Box sx={{ px: 1 }}>
      {alarms.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No alarms found
        </Typography>
      ) : (
        alarms.map(alarm => renderAlarmCard(alarm))
      )}
    </Box>
  );

  // Render alarms - automatically switches between table (desktop) and cards (mobile)
  const renderAlarms = (alarms, showActions = true) => {
    if (isMobile) {
      return renderAlarmCards(alarms);
    }
    return renderAlarmTable(alarms, showActions);
  };

  const renderAlarmTable = (alarms, showActions = true) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Person</TableCell>
            <TableCell>Device</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Notifications</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alarms.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  No alarms found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            alarms.map((alarm) => (
              <TableRow
                key={alarm.id}
                hover
                sx={{
                  backgroundColor: alarm.stateCode === 'sent_to_emergency' ? 'error.light' :
                                   alarm.stateCode === 'countdown' ? 'warning.light' : 'inherit',
                  '&:hover': {
                    backgroundColor: alarm.stateCode === 'sent_to_emergency' ? 'error.main' :
                                     alarm.stateCode === 'countdown' ? 'warning.main' : undefined
                  },
                  // Pulse animation for critical alarms
                  ...(alarm.stateCode === 'sent_to_emergency' && {
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.7 }
                    }
                  })
                }}
              >
                <TableCell>
                  <Chip
                    icon={getStatusIcon(alarm.stateCode)}
                    label={alarm.state}
                    color={getStatusColor(alarm.stateCode)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{alarm.type || 'Unknown'}</TableCell>
                <TableCell>
                  {(() => {
                    const locationInfo = getBestLocationInfo(alarm);
                    if (!locationInfo || !locationInfo.alarmLocation) return '-';
                    return (
                      <Box>
                        {/* Alarm/Signal Location */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" component="span">
                            At: {locationInfo.alarmLocation}
                          </Typography>
                          {locationInfo.isAtHome && (
                            <Tooltip title="Person is at their home location">
                              <Chip
                                label="home"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {/* Person's Home Location (only if different) */}
                        {locationInfo.personHomeLocation && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 0.5 }}>
                            <Home fontSize="small" color="primary" sx={{ fontSize: 16 }} />
                            <Typography variant="caption" color="text.secondary">
                              Home: {locationInfo.personHomeLocation}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {alarm.person ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon fontSize="small" color="action" />
                      {alarm.person}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {alarm.device ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeviceIcon fontSize="small" color="action" />
                      {alarm.device}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title={format(new Date(alarm.firstPressAt), 'PPpp')}>
                    <span>{formatDistanceToNow(new Date(alarm.firstPressAt), { addSuffix: true })}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{alarm.notificationCount || 0}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewAlarm(alarm)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmergencyIcon color="error" />
            {isMobile ? 'Alarms' : 'Alarm Dashboard'}
          </Typography>
          {/* Real-time connection indicator */}
          <Chip
            size="small"
            label={isConnected ? 'Live' : 'Polling'}
            color={isConnected ? 'success' : 'default'}
            variant={isConnected ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Audio toggle */}
          <Tooltip title={audioEnabled ? 'Disable alarm sounds' : 'Enable alarm sounds'}>
            <IconButton
              onClick={handleAudioToggle}
              color={audioEnabled ? 'primary' : 'default'}
              size={isMobile ? 'small' : 'medium'}
            >
              {audioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
            </IconButton>
          </Tooltip>

          {/* Export button */}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
          >
            <MenuItem onClick={() => { handleExportCSV(); setExportAnchorEl(null); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Export CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleExportJSON(); setExportAnchorEl(null); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Export JSON</ListItemText>
            </MenuItem>
          </Menu>

          {/* Refresh button */}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={tabValue === 0 ? loadActiveAndRecentAlarms : loadAlarmHistory}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search alarms by person, device, location, type..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: localSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setLocalSearch(''); setSearchTerm(''); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Quick Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterListIcon color="action" fontSize="small" />
        <Chip
          label={`All (${quickFilterCounts.all})`}
          color={quickFilter === 'all' ? 'primary' : 'default'}
          variant={quickFilter === 'all' ? 'filled' : 'outlined'}
          onClick={() => setQuickFilter('all')}
          size="small"
        />
        <Chip
          icon={<CriticalIcon />}
          label={`Critical (${quickFilterCounts.critical})`}
          color={quickFilter === 'critical' ? 'error' : 'default'}
          variant={quickFilter === 'critical' ? 'filled' : 'outlined'}
          onClick={() => setQuickFilter('critical')}
          size="small"
        />
        <Chip
          icon={<PendingIcon />}
          label={`Pending (${quickFilterCounts.pending})`}
          color={quickFilter === 'pending' ? 'warning' : 'default'}
          variant={quickFilter === 'pending' ? 'filled' : 'outlined'}
          onClick={() => setQuickFilter('pending')}
          size="small"
        />
        <Chip
          icon={<CheckCircleIcon />}
          label={`Resolved (${quickFilterCounts.resolved})`}
          color={quickFilter === 'resolved' ? 'success' : 'default'}
          variant={quickFilter === 'resolved' ? 'filled' : 'outlined'}
          onClick={() => setQuickFilter('resolved')}
          size="small"
        />
        <Chip
          icon={<CancelledIcon />}
          label={`Cancelled (${quickFilterCounts.cancelled})`}
          color={quickFilter === 'cancelled' ? 'default' : 'default'}
          variant={quickFilter === 'cancelled' ? 'filled' : 'outlined'}
          onClick={() => setQuickFilter('cancelled')}
          size="small"
        />
        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>
            Clear Filters
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ActiveIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
                <Typography variant={isMobile ? 'body2' : 'h6'}>Active</Typography>
              </Box>
              <Typography variant={isMobile ? 'h4' : 'h3'} color="error">
                {stats.activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HistoryIcon color="warning" fontSize={isMobile ? 'small' : 'medium'} />
                <Typography variant={isMobile ? 'body2' : 'h6'}>24 Hours</Typography>
              </Box>
              <Typography variant={isMobile ? 'h4' : 'h3'} color="warning.main">
                {stats.recentCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon color="info" fontSize={isMobile ? 'small' : 'medium'} />
                <Typography variant={isMobile ? 'body2' : 'h6'}>Pending</Typography>
              </Box>
              <Typography variant={isMobile ? 'h4' : 'h3'} color="info.main">
                {stats.pendingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
                <Typography variant={isMobile ? 'body2' : 'h6'}>Resolved</Typography>
              </Box>
              <Typography variant={isMobile ? 'h4' : 'h3'} color="success.main">
                {stats.resolvedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Active & Recent
                {activeAlarms.length > 0 && (
                  <Chip label={activeAlarms.length} color="error" size="small" />
                )}
              </Box>
            }
          />
          <Tab label="Alarm History" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Box>
          {/* Active Alarms Section */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                Active Alarms
                {filteredActiveAlarms.length > 0 && (
                  <Chip label={`${filteredActiveAlarms.length}${hasActiveFilters ? ` / ${activeAlarms.length}` : ''}`} color="error" size="small" />
                )}
              </Typography>
            </Box>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} />
              </Box>
            ) : filteredActiveAlarms.length === 0 && activeAlarms.length > 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <FilterListIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No alarms match your filters</Typography>
                <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>Clear Filters</Button>
              </Box>
            ) : filteredActiveAlarms.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography color="text.secondary">No active alarms - all is well!</Typography>
              </Box>
            ) : (
              renderAlarms(filteredActiveAlarms)
            )}
          </Paper>

          {/* Recent Alarms Section */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                Last 24 Hours
                <Chip label={`${filteredRecentAlarms.length}${hasActiveFilters ? ` / ${recentAlarms.length}` : ''}`} color="primary" size="small" />
              </Typography>
            </Box>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} />
              </Box>
            ) : filteredRecentAlarms.length === 0 && hasActiveFilters ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <FilterListIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No alarms match your filters</Typography>
                <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>Clear Filters</Button>
              </Box>
            ) : (
              renderAlarms(filteredRecentAlarms)
            )}
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Paper>
            {/* Filters */}
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
              {/* Date Range Quick Presets */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
                  Quick:
                </Typography>
                <Chip
                  label="Last 24h"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDateFrom(subHours(new Date(), 24).toISOString());
                    setDateTo(new Date().toISOString());
                    setHistoryPage(0);
                  }}
                />
                <Chip
                  label="Last 7 days"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDateFrom(subDays(new Date(), 7).toISOString());
                    setDateTo(new Date().toISOString());
                    setHistoryPage(0);
                  }}
                />
                <Chip
                  label="Last 30 days"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDateFrom(subDays(new Date(), 30).toISOString());
                    setDateTo(new Date().toISOString());
                    setHistoryPage(0);
                  }}
                />
                <Chip
                  label="Last 90 days"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDateFrom(subDays(new Date(), 90).toISOString());
                    setDateTo(new Date().toISOString());
                    setHistoryPage(0);
                  }}
                />
                {(dateFrom || dateTo) && (
                  <Chip
                    label="Clear dates"
                    size="small"
                    color="secondary"
                    onDelete={() => {
                      setDateFrom(null);
                      setDateTo(null);
                      setHistoryPage(0);
                    }}
                  />
                )}
              </Box>

              <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <DateTimePicker
                    label="From Date"
                    value={dateFrom ? new Date(dateFrom) : null}
                    onChange={(date) => {
                      setDateFrom(date ? date.toISOString() : null);
                      setHistoryPage(0);
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DateTimePicker
                    label="To Date"
                    value={dateTo ? new Date(dateTo) : null}
                    onChange={(date) => {
                      setDateTo(date ? date.toISOString() : null);
                      setHistoryPage(0);
                    }}
                    minDate={dateFrom ? new Date(dateFrom) : undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    options={locations}
                    getOptionLabel={(option) => option.friendlyName || ''}
                    value={selectedLocation}
                    onChange={(e, value) => {
                      setSelectedLocation(value);
                      setHistoryPage(0);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Location" size="small" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      label="Status"
                      onChange={(e) => {
                        setSelectedStatus(e.target.value);
                        setHistoryPage(0);
                      }}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="initiated">Initiated</MenuItem>
                      <MenuItem value="countdown">Countdown</MenuItem>
                      <MenuItem value="routed">Routed</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_call">In Call</MenuItem>
                      <MenuItem value="sent_to_emergency">Sent to Emergency</MenuItem>
                      <MenuItem value="acknowledged">Acknowledged</MenuItem>
                      <MenuItem value="dispatched">Dispatched</MenuItem>
                      <MenuItem value="finished">Finished</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="dropped">Dropped</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedLocation(null);
                      setSelectedStatus('');
                      setDateFrom(null);
                      setDateTo(null);
                      setHistoryPage(0);
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    fullWidth
                    startIcon={<ClearIcon />}
                  >
                    Clear All
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* History Table */}
            {loading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} />
              </Box>
            ) : historyAlarms.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No alarms found for the selected filters</Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedLocation(null);
                    setSelectedStatus('');
                    setDateFrom(null);
                    setDateTo(null);
                    setHistoryPage(0);
                  }}
                  sx={{ mt: 1 }}
                >
                  Clear Filters
                </Button>
              </Box>
            ) : (
              <>
                {renderAlarms(historyAlarms)}
                <TablePagination
                  component="div"
                  count={historyTotal}
                  page={historyPage}
                  onPageChange={(e, page) => setHistoryPage(page)}
                  rowsPerPage={historyRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setHistoryRowsPerPage(parseInt(e.target.value, 10));
                    setHistoryPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </Paper>
        </LocalizationProvider>
      )}

    </Box>
  );
};

export default AlarmsDashboard;
